import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE } from '../config';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../components/LoadingSpinner';
import { loginSuccess } from '../slices/authSlice';
import { navigateAfterMemberLogin } from '../utils/memberPostLogin';
import { getPostLoginPath } from '../utils/postLoginRedirect';
import {
  getOtpResendSuccessMessage,
  getOtpSubtitle,
  storeOtpDelivery,
} from '../utils/otpDeliveryMessage';
import './Login.css';

const RESEND_COOLDOWN_SEC = 30;
const UNAUTHORISED_MSG =
  'Account not set up. Contact your HR admin.';
const MEMBER_INACTIVE_MSG =
  'Your account is inactive. Contact your HR admin.';
const MEMBER_OFF_ROSTER_MSG =
  'Your employer access has ended. Contact your HR admin.';
const ROSTER_UNAVAILABLE_MSG =
  'Unable to verify your employer access right now. Please try again.';

const MEMBER_ACCESS_DENIED_CODES = new Set([
  'USER_NOT_PROVISIONED',
  'NOT_ON_ALLOWLIST',
  'MEMBER_INACTIVE',
  'MEMBER_OFF_ROSTER',
]);

function memberAccessDeniedMessage(data) {
  if (data?.code === 'MEMBER_INACTIVE') return MEMBER_INACTIVE_MSG;
  if (data?.code === 'MEMBER_OFF_ROSTER') return MEMBER_OFF_ROSTER_MSG;
  if (data?.code === 'ROSTER_UNAVAILABLE') return ROSTER_UNAVAILABLE_MSG;
  return UNAUTHORISED_MSG;
}

function clearAuthSession() {
  sessionStorage.removeItem('auth_email');
  sessionStorage.removeItem('auth_otp_mode');
  sessionStorage.removeItem('auth_lookup_ok');
  sessionStorage.removeItem('auth_on_roster');
  sessionStorage.removeItem('auth_has_account');
  sessionStorage.removeItem('auth_otp_delivery');
  sessionStorage.removeItem('auth_otp_email_failed');
}

const Otp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendNotice, setResendNotice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [preflightLoading, setPreflightLoading] = useState(true);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const roles = useSelector((state) => state.auth.roles);
  const authKind = useSelector((state) => state.auth.authKind);

  const email = sessionStorage.getItem('auth_email');
  const otpMode = sessionStorage.getItem('auth_otp_mode') || 'login';
  const otpDeliveryMode = sessionStorage.getItem('auth_otp_delivery') || 'email';

  function redirectToLoginUnauthorised(message) {
    clearAuthSession();
    navigate('/login', { replace: true, state: { loginMessage: message || UNAUTHORISED_MSG } });
  }

  useEffect(() => {
    if (token) {
      navigate(getPostLoginPath(roles, location.state?.from, authKind || 'member'), { replace: true });
      return;
    }

    if (!email || sessionStorage.getItem('auth_lookup_ok') !== '1') {
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;
    setPreflightLoading(true);

    fetch(`${API_BASE}/api/auth/member-lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || MEMBER_ACCESS_DENIED_CODES.has(data.code)) {
          redirectToLoginUnauthorised(memberAccessDeniedMessage(data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not verify your account. Please go back and try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setPreflightLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  async function handleResendOtp() {
    if (!email || isResending || resendCooldown > 0) return;

    setIsResending(true);
    setError('');
    setResendNotice('');

    try {
      const res = await fetch(`${API_BASE}/api/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, mode: otpMode }),
      });

      let data = {};
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(
          !res.ok ? `Request failed (${res.status}).` : 'Invalid response from server.'
        );
      }

      if (!res.ok) {
        if (MEMBER_ACCESS_DENIED_CODES.has(data.code)) {
          redirectToLoginUnauthorised(memberAccessDeniedMessage(data));
          return;
        }
        if (res.status === 503) {
          setError(
            'OTP could not be emailed. In development, check the API server log for the code, or ask your admin to configure email.'
          );
        } else {
          setError(data.error || data.message || 'Could not resend OTP. Please try again.');
        }
        return;
      }

      setOtp('');
      storeOtpDelivery(data.delivery, data.emailDeliveryFailed);
      setResendNotice(getOtpResendSuccessMessage(data.delivery, data.emailDeliveryFailed));
      setResendCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      setError(
        err?.message?.includes('Failed to fetch')
          ? 'Cannot reach the server. Check your network and try again.'
          : err?.message || 'Could not resend OTP. Please try again.'
      );
    } finally {
      setIsResending(false);
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setError('Enter valid 6-digit OTP');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setResendNotice('');

    try {
      const res = await fetch(`${API_BASE}/api/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (MEMBER_ACCESS_DENIED_CODES.has(data.code)) {
          redirectToLoginUnauthorised(memberAccessDeniedMessage(data));
          return;
        }
        setError(data.error || 'Invalid OTP');
        return;
      }

      if (data.existingUser) {
        clearAuthSession();
        dispatch(loginSuccess({
          token: data.token,
          user: data.user,
          roles: data.roles || { isDoctor: false, isOps: false },
          authKind: data.authKind || 'member',
        }));
        sessionStorage.setItem('app_user', JSON.stringify(data.user));
        setTimeout(
          () =>
            navigateAfterMemberLogin(navigate, {
              needsProfileCompletion: data.needsProfileCompletion,
              needsMemberOnboarding: data.needsMemberOnboarding,
              roles: data.roles,
              from: location.state?.from,
              authKind: data.authKind || 'member',
            }),
          50
        );
      } else {
        redirectToLoginUnauthorised();
      }
    } catch (err) {
      setError('Server error');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (preflightLoading) {
    return (
      <div className="proceed-page">
        <LoadingSpinner className="ops-loading" label="Verifying account" />
      </div>
    );
  }

  return (
    <div className="proceed-page">
      <div className="proceed-bg-shapes" aria-hidden="true">
        <span className="proceed-shape proceed-shape-1"></span>
        <span className="proceed-shape proceed-shape-2"></span>
      </div>

      <Link to="/" className="proceed-logo">
        <img
          src="/images/unaffectedLogo.png"
          alt="Unaffected Healthcare Logo"
          className="proceed-logo-image"
        />
        <span className="proceed-logo-text">Unaffected Healthcare</span>
      </Link>

      <div className="login-page">
        <div className="login-card-wrap">
          <div className="login-card">
            <h1 className="login-title login-title-form">Enter OTP</h1>
            <p className="login-subtitle">{getOtpSubtitle(email)}</p>
            {(otpDeliveryMode === 'log_only' || otpDeliveryMode === 'skipped') && (
              <p className="login-inline-error" role="status" style={{ marginBottom: '0.75rem' }}>
                Email delivery is off for this environment. Check the API server console for your 6-digit code.
              </p>
            )}

            <form onSubmit={handleVerify} className="login-form">
              <div className="form-group">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="login-input"
                />
              </div>
              {error && <p className="login-inline-error">{error}</p>}

              <button
                type="submit"
                className="login-btn-submit"
                disabled={isSubmitting || isResending}
              >
                {isSubmitting ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>

            {resendNotice && (
              <p className="login-inline-success" role="status">
                {resendNotice}
              </p>
            )}

            <p className="login-otp-resend">
              Didn&apos;t receive the code?{' '}
              <button
                type="button"
                className="login-link-back login-otp-resend-btn"
                onClick={handleResendOtp}
                disabled={isResending || isSubmitting || resendCooldown > 0}
              >
                {isResending
                  ? 'Sending…'
                  : resendCooldown > 0
                    ? `Resend OTP in ${resendCooldown}s`
                    : 'Resend OTP'}
              </button>
            </p>

            <button
              type="button"
              className="login-link-back"
              style={{ marginTop: '0.75rem', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => navigate('/login')}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Otp;
